import { eachDayOfInterval, format, isAfter } from 'date-fns'
import { BarChart3, LineChart as LineChartIcon } from 'lucide-react'
import { type CSSProperties, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/sv/chart'
import { cn } from '@/lib/utils'
import { getUsageTypeColor, getUsageTypeLabel } from '@/routes/usage/constants'
import { formatMoney, getLastCompletedDay } from '@/routes/usage/utils'

type ChartDay = {
  date: Date | string | number
  services: {
    idService?: string | null
    serviceName?: string | null
    totalGross: number
    usageByType: {
      type: string
      gross: number
    }[]
  }[]
}

type UsageChartProps = {
  days: ChartDay[]
  projectedDailyAmount: number
  periodStart: Date | null
  periodEnd: Date | null
  highlightedType: string | null
  showControls?: boolean
}

const TooltipContent = ({
  chartConfig,
  isCumulative,
  ...rest
}: {
  chartConfig: ChartConfig
  isCumulative: boolean
} & Record<string, unknown>) => (
  <ChartTooltipContent
    {...rest}
    payloadSorter={(itemA, itemB) => Number(itemB.value) - Number(itemA.value)}
    formatter={(value, category, item, idx, payloads) => {
      const segmentKey = String(category)
      if (segmentKey === '__projected') {
        return null
      }

      const isForecast = segmentKey === 'forecast'
      const segmentLabel = isForecast
        ? 'Forecasted cost'
        : (chartConfig[segmentKey]?.label ?? segmentKey)

      // Use forecastDisplay (remainder) if available for correct totals
      const forecastDisplayValue =
        payloads &&
        typeof payloads === 'object' &&
        'forecastDisplay' in payloads
          ? Number(payloads.forecastDisplay)
          : null
      const displayValue =
        isForecast && forecastDisplayValue !== null
          ? forecastDisplayValue
          : Number(value)

      const rowTotal =
        payloads && typeof payloads === 'object'
          ? Object.entries(payloads)
              .filter(
                ([k, v]) =>
                  typeof v === 'number' &&
                  k !== '__projected' &&
                  k !== 'forecastDisplay'
              )
              .reduce((acc, [k, v]) => {
                // Use forecastDisplay instead of forecast for total
                if (k === 'forecast' && forecastDisplayValue !== null) {
                  return acc + forecastDisplayValue
                }
                return acc + Number(v)
              }, 0)
          : 0

      return (
        <div className="grid w-full grid-cols-[auto_auto_1fr] items-center gap-2">
          {idx === 0 ? (
            <>
              <span className="col-start-1 col-span-2 font-bold">
                {isCumulative ? 'Cumulative' : 'Total'}
              </span>
              <code className="justify-self-end font-bold">
                {formatMoney(rowTotal)}
              </code>
            </>
          ) : null}
          <div
            className="size-2.5 rounded-[2px] border-(--color-border) bg-(--color-bg)"
            style={
              {
                '--color-bg': item.color,
                '--color-border': item.color,
              } as CSSProperties
            }
          />
          <div>{segmentLabel}</div>
          <code className="justify-self-end">{formatMoney(displayValue)}</code>
        </div>
      )
    }}
    payloadFilter={(payload) => {
      const key = String(payload.dataKey ?? '')
      return (
        key !== '__projected' &&
        key !== 'forecastDisplay' &&
        Number(payload.value ?? 0) !== 0
      )
    }}
  />
)

export const UsageChart = ({
  days,
  projectedDailyAmount,
  periodStart,
  periodEnd,
  highlightedType,
  showControls = true,
}: UsageChartProps) => {
  const [isCumulative, setIsCumulative] = useState(false)
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar')

  const actualDataByDate = useMemo(() => {
    const map = new Map<string, ChartDay>()
    for (const day of days) {
      const key = format(new Date(day.date), 'yyyy-MM-dd')
      map.set(key, day)
    }
    return map
  }, [days])

  const lastCompletedDay = useMemo(() => getLastCompletedDay(), [])

  // Build daily data points, chart config, and keys
  const { dailyData, chartConfig, allKeys } = useMemo(() => {
    if (!periodStart || !periodEnd) {
      return { dailyData: [], chartConfig: {} as ChartConfig, allKeys: [] }
    }

    const allDates = eachDayOfInterval({ start: periodStart, end: periodEnd })
    const keySet = new Set<string>()

    const points = allDates.map((date) => {
      const dateKey = format(date, 'yyyy-MM-dd')
      const dayLabel = format(date, 'MMM dd')
      const actualDay = actualDataByDate.get(dateKey)
      const isFuture = isAfter(date, lastCompletedDay)

      const row: Record<string, number | string> = { day: dayLabel }

      if (actualDay) {
        let dayTotal = 0
        for (const svc of actualDay.services) {
          for (const u of svc.usageByType) {
            keySet.add(u.type)
            row[u.type] = ((row[u.type] as number) ?? 0) + u.gross
            dayTotal += u.gross
          }
        }
        // Incomplete day: forecast is the remaining projected amount
        if (isFuture && projectedDailyAmount > dayTotal) {
          row.forecast = projectedDailyAmount - dayTotal
        }
      } else if (isFuture && projectedDailyAmount > 0) {
        row.__projected = 1
        row.forecast = projectedDailyAmount
      }

      return row
    })

    // Sort keys by total gross ascending (bottom of stack = highest, matches table)
    const keyTotals = new Map<string, number>()
    for (const point of points) {
      for (const key of keySet) {
        const val = (point[key] as number) ?? 0
        keyTotals.set(key, (keyTotals.get(key) ?? 0) + val)
      }
    }
    const keys = Array.from(keySet).sort(
      (a, b) => (keyTotals.get(a) ?? 0) - (keyTotals.get(b) ?? 0)
    )

    // Ensure all points have all usage type keys (default to 0)
    // so area lines are continuous instead of having gaps
    for (const point of points) {
      for (const key of keys) {
        if (point[key] === undefined) {
          point[key] = 0
        }
      }
    }

    const config: ChartConfig = {}

    for (const type of keys) {
      config[type] = {
        label: getUsageTypeLabel(type),
        color: getUsageTypeColor(type),
      }
    }

    config.forecast = {
      label: 'Forecasted cost',
      color: 'var(--muted-foreground)',
    }

    return { dailyData: points, chartConfig: config, allKeys: keys }
  }, [
    periodStart,
    periodEnd,
    actualDataByDate,
    projectedDailyAmount,
    lastCompletedDay,
  ])

  // Convert daily data to cumulative bars
  // Projected days: actual base (stacked by type) + single aggregated forecast bar
  const cumulativeData = useMemo(() => {
    if (!isCumulative) {
      return null
    }

    const cumTotals = new Map<string, number>()
    let cumForecast = 0

    return dailyData.map((point) => {
      const row: Record<string, number | string> = { day: point.day }
      const isFullyProjected = point.__projected === 1
      const hasForecast = (point.forecast as number) > 0

      // Accumulate actual usage totals (even for projected days, to track running sum)
      for (const key of allKeys) {
        const dailyVal = (point[key] as number) ?? 0
        const prev = cumTotals.get(key) ?? 0
        cumTotals.set(key, prev + dailyVal)
      }

      const actualCumTotal = allKeys.reduce(
        (sum, key) => sum + (cumTotals.get(key) ?? 0),
        0
      )

      if (isFullyProjected) {
        // Fully projected: single forecast bar including actual base
        cumForecast += projectedDailyAmount
        row.forecast = actualCumTotal + cumForecast
        row.__projected = 1
      } else {
        // Actual/incomplete day: show cumulative type bars
        for (const key of allKeys) {
          row[key] = cumTotals.get(key) ?? 0
        }
        if (hasForecast) {
          cumForecast += point.forecast as number
          row.forecast = cumForecast
        }
      }

      return row
    })
  }, [dailyData, isCumulative, allKeys, projectedDailyAmount])

  // For daily line mode: use full projected amount for forecast (non-stacked)
  const lineData = useMemo(() => {
    if (chartType !== 'line' || isCumulative) {
      return null
    }
    return dailyData.map((point) => {
      const row: Record<string, number | string> = { ...point }
      // Incomplete day: use full projected amount for area positioning,
      // but keep the remainder as forecastDisplay for the tooltip
      if (row.forecast !== undefined && row.__projected !== 1) {
        row.forecastDisplay = row.forecast
        row.forecast = projectedDailyAmount
      }
      return row
    })
  }, [dailyData, chartType, isCumulative, projectedDailyAmount])

  // Final chart data
  const chartDataFinal = useMemo(() => {
    if (chartType === 'line' && !isCumulative && lineData) {
      return lineData
    }
    if (isCumulative && cumulativeData) {
      return cumulativeData
    }
    return dailyData
  }, [dailyData, cumulativeData, lineData, isCumulative, chartType])

  if (dailyData.length === 0) {
    return (
      <div className="px-6 py-8">
        <p className="text-center text-muted-foreground">
          No usage recorded for this period
        </p>
      </div>
    )
  }

  return (
    <div className="px-6">
      {showControls && (
        <div className="flex items-center justify-end pb-4 print:hidden">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-md border p-0.5">
              <button
                type="button"
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  !isCumulative
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setIsCumulative(false)}
              >
                Daily
              </button>
              <button
                type="button"
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  isCumulative
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setIsCumulative(true)}
              >
                Cumulative
              </button>
            </div>
            <div className="flex items-center gap-1 rounded-md border p-0.5">
              <button
                type="button"
                className={cn(
                  'rounded p-1.5 transition-colors',
                  chartType === 'bar'
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setChartType('bar')}
              >
                <BarChart3 className="size-3.5" />
              </button>
              <button
                type="button"
                className={cn(
                  'rounded p-1.5 transition-colors',
                  chartType === 'line'
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setChartType('line')}
              >
                <LineChartIcon className="size-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <ChartContainer
        config={chartConfig}
        className="min-h-[250px] max-h-[350px] w-full [&_.recharts-responsive-container]:max-h-[350px] print:min-h-[180px] print:max-h-[220px] print:[&_.recharts-responsive-container]:max-h-[220px]"
      >
        {chartType === 'bar' ? (
          <BarChart accessibilityLayer data={chartDataFinal}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="day"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              tickMargin={10}
              orientation="right"
              axisLine={false}
              tickFormatter={(value) => `$${value}`}
            />
            <ChartTooltip
              content={
                <TooltipContent
                  chartConfig={chartConfig}
                  isCumulative={isCumulative}
                />
              }
            />
            {allKeys.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                fill={chartConfig[key]?.color}
                stackId="a"
                opacity={highlightedType && highlightedType !== key ? 0.15 : 1}
                className="transition-opacity duration-150"
              />
            ))}
            {projectedDailyAmount > 0 && (
              <Bar
                dataKey="forecast"
                fill="var(--muted-foreground)"
                stackId="a"
                opacity={highlightedType ? 0.05 : 0.15}
                className="transition-opacity duration-150"
              />
            )}
          </BarChart>
        ) : (
          <AreaChart accessibilityLayer data={chartDataFinal}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="day"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              tickMargin={10}
              orientation="right"
              axisLine={false}
              tickFormatter={(value) => `$${value}`}
            />
            <ChartTooltip
              content={
                <TooltipContent
                  chartConfig={chartConfig}
                  isCumulative={isCumulative}
                />
              }
            />
            {allKeys.map((key) => (
              <Area
                key={key}
                dataKey={key}
                type="monotone"
                fill={chartConfig[key]?.color}
                stroke={chartConfig[key]?.color}
                stackId="a"
                fillOpacity={
                  highlightedType && highlightedType !== key ? 0.05 : 0.3
                }
                strokeOpacity={
                  highlightedType && highlightedType !== key ? 0.15 : 1
                }
                className="transition-opacity duration-150"
              />
            ))}
            {projectedDailyAmount > 0 && (
              <Area
                dataKey="forecast"
                type="monotone"
                fill="var(--muted-foreground)"
                stroke="var(--muted-foreground)"
                stackId={isCumulative ? 'a' : 'forecast'}
                fillOpacity={highlightedType ? 0.02 : 0.1}
                strokeOpacity={highlightedType ? 0.05 : 0.15}
                strokeDasharray="4 4"
                className="transition-opacity duration-150"
              />
            )}
          </AreaChart>
        )}
      </ChartContainer>
    </div>
  )
}
